import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { Customer } from '@/lib/models/customer';
import type { ODataResponse } from '@/lib/models/common';
import { CustomerIntegration } from '~/lib/models/customerIntegration';

/**
 * Interfaccia per l'endpoint dei Customers
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 */
export interface CustomerEndpoints {
	/**
	 * Recupera la lista dei clienti con query OData
	 * @param query - Query string OData
	 * @returns Promise con response OData contenente array di Customer
	 */
	getCustomers(query: string): Promise<FetchResponse<ODataResponse<Customer>>>;

	/**
	 * Recupera un singolo cliente per ID
	 * @param id - ID del cliente
	 * @returns Promise con singolo Customer
	 */
	getCustomer(id: number): Promise<FetchResponse<Customer>>;

	/**
	 * Aggiorna un cliente esistente
	 * @param id - ID del cliente da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con Customer aggiornato
	 */
	updateCustomer(id: number, payload: Customer): Promise<FetchResponse<Customer>>;

	/**
	 * Crea un nuovo cliente
	 * @param payload - Dati del nuovo cliente
	 * @returns Promise con Customer creato
	 */
	postCustomer(payload: Customer): Promise<FetchResponse<Customer>>;

	/**
	 * Elimina un cliente
	 * @param id - ID del cliente da eliminare
	 * @returns Promise con response di conferma
	 */
	deleteCustomer(id: number): Promise<FetchResponse<void>>;

	/**
	 * Recupera le integrazioni di un cliente
	 * @param customerId - ID del cliente
	 * @returns Promise con array di CustomerIntegration
	 */
	getIntegrations(customerId: number): Promise<FetchResponse<ODataResponse<CustomerIntegration>>>;

	/**
	 * Genera file Excel per i clienti
	 * @param query - Query string per i filtri
	 * @returns Promise con blob del file Excel
	 */
	generateExcel(query: string): Promise<FetchResponse<Blob>>;
}

/**
 * Implementazione tipizzata degli endpoint per i Customers
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 */
const customerEndpoints: CustomerEndpoints = {
	getCustomers(query: string): Promise<FetchResponse<ODataResponse<Customer>>> {
		return api.get(`/odata/v1/Customers${query}`);
	},

	getCustomer(id: number): Promise<FetchResponse<Customer>> {
		return api.get(`/odata/v1/Customers/${id}`);
	},

	updateCustomer(id: number, payload: Customer): Promise<FetchResponse<Customer>> {
		return api.put(`/api/v1/Customers/${id}`, payload);
	},

	postCustomer(payload: Customer): Promise<FetchResponse<Customer>> {
		return api.post('/api/v1/Customers', payload);
	},

	deleteCustomer(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/Customers/${id}`);
	},

	getIntegrations(customerId: number): Promise<FetchResponse<ODataResponse<CustomerIntegration>>> {
		return api.get(`/odata/v1/CustomerIntegrations?$filter=(sourceId eq ${customerId})`);
	},

	generateExcel(query: string): Promise<FetchResponse<Blob>> {
		return api.get(`/api/v1/Customers/actions/generateExcel/${query}`, {
			responseType: 'blob',
		});
	},
};

export default customerEndpoints;
